import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

function Notifications() {
  const { user } = useAuth();
  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingSetting, setEditingSetting] = useState(null);
  const [formData, setFormData] = useState({
    userId: '',
    companyId: '',
    emailEnabled: true,
    whatsappEnabled: false,
    notifyOnAssignment: true,
    notifyOnQcRequest: true,
    notifyOnQcResult: true,
    notifyOnExtension: true,
    notifyOnDeadline: true,
    deadlineReminderDays: 3
  });
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);

  useEffect(() => {
    fetchSettings();
    if (user.userType === 'admin' || user.userType === 'tis') {
      fetchUsers();
      fetchCompanies();
    }
  }, [user.userType]);

  const fetchSettings = async () => {
    try {
      const response = await api.getNotificationSettings();
      setSettings(response.data.settings || response.data || []);
    } catch (error) {
      console.error('Bildirim ayarları yüklenemedi:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.getUsers();
      setUsers(response.data.users || response.data || []);
    } catch (error) {
      console.error('Kullanıcılar yüklenemedi:', error);
    }
  };

  const fetchCompanies = async () => {
    try {
      const response = await api.getCompanies();
      setCompanies(response.data.companies || response.data || []);
    } catch (error) {
      console.error('Firmalar yüklenemedi:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = { ...formData };
      if (!data.userId) delete data.userId;
      if (!data.companyId) delete data.companyId;

      if (editingSetting) {
        await api.updateNotificationSetting(editingSetting.id, data);
      } else {
        await api.createNotificationSetting(data);
      }
      fetchSettings();
      closeModal();
    } catch (error) {
      alert(error.response?.data?.message || 'İşlem başarısız');
    }
  };

  const handleDelete = async (id) => {
    if (user.userType !== 'admin') {
      alert('Sadece admin silme yapabilir!');
      return;
    }
    if (window.confirm('Bu ayarı silmek istediğinize emin misiniz?')) {
      try {
        await api.deleteNotificationSetting(id);
        fetchSettings();
      } catch (error) {
        alert(error.response?.data?.message || 'Silme başarısız');
      }
    }
  };

  const openModal = (setting = null) => {
    if (setting) {
      setEditingSetting(setting);
      setFormData({
        userId: setting.userId || '',
        companyId: setting.companyId || '',
        emailEnabled: setting.emailEnabled,
        whatsappEnabled: setting.whatsappEnabled,
        notifyOnAssignment: setting.notifyOnAssignment,
        notifyOnQcRequest: setting.notifyOnQcRequest,
        notifyOnQcResult: setting.notifyOnQcResult,
        notifyOnExtension: setting.notifyOnExtension,
        notifyOnDeadline: setting.notifyOnDeadline,
        deadlineReminderDays: setting.deadlineReminderDays || 3
      });
    } else {
      setEditingSetting(null);
      setFormData({
        userId: '',
        companyId: '',
        emailEnabled: true,
        whatsappEnabled: false,
        notifyOnAssignment: true,
        notifyOnQcRequest: true,
        notifyOnQcResult: true,
        notifyOnExtension: true,
        notifyOnDeadline: true,
        deadlineReminderDays: 3
      });
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingSetting(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Bildirim Ayarları</h1>
          <p className="text-gray-600 mt-1">E-posta ve WhatsApp bildirimlerini yönetin</p>
        </div>
        <button
          onClick={() => openModal()}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
        >
          + Yeni Ayar
        </button>
      </div>

      {/* Info Card */}
      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <h3 className="font-semibold text-green-900 mb-2">WhatsApp Bildirimleri Hakkında</h3>
        <p className="text-sm text-green-800">
          WhatsApp bildirimleri ücretsiz wa.me linki üzerinden çalışır. Bildirim geldiğinde
          kullanıcıya hazır mesaj içeren bir WhatsApp linki gönderilir.
        </p>
      </div>

      {/* Settings Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Kullanıcı/Firma
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Kanallar
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Bildirim Türleri
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                İşlemler
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {settings.length === 0 ? (
              <tr>
                <td colSpan="4" className="px-6 py-8 text-center text-gray-500">
                  Henüz bildirim ayarı eklenmemiş
                </td>
              </tr>
            ) : (
              settings.map((setting) => (
                <tr key={setting.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div>
                      {setting.User && (
                        <div className="text-sm font-medium text-gray-900">
                          {setting.User.name}
                          <span className="ml-2 text-xs text-gray-500">({setting.User.email})</span>
                        </div>
                      )}
                      {setting.Company && (
                        <div className="text-sm text-blue-600">
                          Firma: {setting.Company.name}
                        </div>
                      )}
                      {!setting.User && !setting.Company && (
                        <span className="text-sm text-gray-500">Genel Ayar</span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex space-x-2">
                      {setting.emailEnabled && (
                        <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                          E-posta
                        </span>
                      )}
                      {setting.whatsappEnabled && (
                        <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                          WhatsApp
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-wrap gap-1">
                      {setting.notifyOnAssignment && (
                        <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-700 rounded">İş Atama</span>
                      )}
                      {setting.notifyOnQcRequest && (
                        <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-700 rounded">QC Talebi</span>
                      )}
                      {setting.notifyOnQcResult && (
                        <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-700 rounded">QC Sonucu</span>
                      )}
                      {setting.notifyOnExtension && (
                        <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-700 rounded">Süre Uzatma</span>
                      )}
                      {setting.notifyOnDeadline && (
                        <span className="px-2 py-0.5 text-xs bg-yellow-100 text-yellow-700 rounded">
                          Deadline ({setting.deadlineReminderDays} gün)
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => openModal(setting)}
                      className="text-blue-600 hover:text-blue-900 mr-3"
                    >
                      Düzenle
                    </button>
                    {user.userType === 'admin' && (
                      <button
                        onClick={() => handleDelete(setting.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Sil
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">
              {editingSetting ? 'Bildirim Ayarı Düzenle' : 'Yeni Bildirim Ayarı'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              {(user.userType === 'admin' || user.userType === 'tis') && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Kullanıcı (Opsiyonel)
                    </label>
                    <select
                      value={formData.userId}
                      onChange={(e) => setFormData({ ...formData, userId: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">Seçilmedi</option>
                      {users.map((u) => (
                        <option key={u.id} value={u.id}>
                          {u.name} ({u.email})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Firma (Opsiyonel)
                    </label>
                    <select
                      value={formData.companyId}
                      onChange={(e) => setFormData({ ...formData, companyId: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">Seçilmedi</option>
                      {companies.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </>
              )}

              <div className="border-t pt-4">
                <h3 className="font-medium text-gray-900 mb-3">Bildirim Kanalları</h3>
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.emailEnabled}
                      onChange={(e) => setFormData({ ...formData, emailEnabled: e.target.checked })}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700">E-posta Bildirimleri</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.whatsappEnabled}
                      onChange={(e) => setFormData({ ...formData, whatsappEnabled: e.target.checked })}
                      className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700">WhatsApp Bildirimleri</span>
                  </label>
                </div>
              </div>

              <div className="border-t pt-4">
                <h3 className="font-medium text-gray-900 mb-3">Bildirim Türleri</h3>
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.notifyOnAssignment}
                      onChange={(e) => setFormData({ ...formData, notifyOnAssignment: e.target.checked })}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700">İş Ataması</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.notifyOnQcRequest}
                      onChange={(e) => setFormData({ ...formData, notifyOnQcRequest: e.target.checked })}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700">QC Talebi</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.notifyOnQcResult}
                      onChange={(e) => setFormData({ ...formData, notifyOnQcResult: e.target.checked })}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700">QC Sonucu</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.notifyOnExtension}
                      onChange={(e) => setFormData({ ...formData, notifyOnExtension: e.target.checked })}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700">Süre Uzatma Talebi</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.notifyOnDeadline}
                      onChange={(e) => setFormData({ ...formData, notifyOnDeadline: e.target.checked })}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700">Deadline Hatırlatma</span>
                  </label>
                </div>
              </div>

              {formData.notifyOnDeadline && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Deadline Hatırlatma (Gün Önce)
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="30"
                    value={formData.deadlineReminderDays}
                    onChange={(e) => setFormData({ ...formData, deadlineReminderDays: parseInt(e.target.value) || 3 })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              )}

              <div className="flex justify-end space-x-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  İptal
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  {editingSetting ? 'Güncelle' : 'Oluştur'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Notifications;
